import { apiClient } from '@/api/client'
import {
  NOTIFICATIONS,
  NOTIFICATION_TEMPLATES,
  notificationsUnreadCountPath,
  notificationCompanyFacetsPath,
  notificationMarkReadPath,
  notificationTemplateByIdPath,
} from '@/api/endpoints'
import type {
  Notification,
  NotificationCompanyFacet,
  NotificationQueryParams,
  NotificationTemplate,
  NotificationTemplateUpdatePayload,
  UnreadCountResponse,
} from '@/types'
import type { ApiResponseWrapper } from '@/types/api.types'
import type { PaginatedResponse } from '@/types'

function toPaginatedNotifications(inner: {
  data: Notification[]
  total: number
  page: number
  limit: number
  totalPages?: number
}): PaginatedResponse<Notification> {
  const total = Number(inner.total) || 0
  const limit = Number(inner.limit) || 10
  const totalPages =
    inner.totalPages != null && inner.totalPages > 0
      ? inner.totalPages
      : Math.ceil(total / limit) || 0
  return {
    data: Array.isArray(inner.data) ? inner.data : [],
    total,
    page: Number(inner.page) || 1,
    limit,
    totalPages,
  }
}

export async function getUnreadCount(): Promise<number> {
  const { data } = await apiClient.get<ApiResponseWrapper<UnreadCountResponse>>(
    notificationsUnreadCountPath()
  )
  const payload = data?.data
  if (payload && typeof payload === 'object' && typeof payload.count === 'number') {
    return payload.count
  }
  return 0
}

export async function getNotifications(
  params?: NotificationQueryParams
): Promise<PaginatedResponse<Notification>> {
  const { data } = await apiClient.get<
    ApiResponseWrapper<{
      data: Notification[]
      total: number
      page: number
      limit: number
      totalPages?: number
    }>
  >(NOTIFICATIONS, { params })
  const payload = data?.data
  if (payload && typeof payload === 'object' && Array.isArray(payload.data)) {
    return toPaginatedNotifications(payload)
  }
  return toPaginatedNotifications({
    data: [],
    total: 0,
    page: 1,
    limit: params?.limit ?? 10,
  })
}

export async function getNotificationCompanyFacets(): Promise<
  NotificationCompanyFacet[]
> {
  const { data } = await apiClient.get<
    ApiResponseWrapper<NotificationCompanyFacet[]>
  >(notificationCompanyFacetsPath())
  return Array.isArray(data?.data) ? data.data : []
}

export async function markAsRead(id: string): Promise<void> {
  await apiClient.put(notificationMarkReadPath(id))
}

export interface MarkNotificationsReadFilter {
  companyId?: string
  types?: string[]
  excludeTypes?: string[]
}

export async function markAllAsRead(filter: MarkNotificationsReadFilter = {}): Promise<void> {
  await apiClient.put(`${NOTIFICATIONS}/read-all`, filter)
}

export async function deleteReadNotifications(): Promise<void> {
  await apiClient.delete(`${NOTIFICATIONS}/read`)
}

export async function getNotificationTemplates(): Promise<NotificationTemplate[]> {
  const { data } = await apiClient.get<ApiResponseWrapper<NotificationTemplate[]>>(
    NOTIFICATION_TEMPLATES
  )
  const list = data?.data
  return Array.isArray(list) ? list : []
}

export async function getNotificationTemplate(id: string): Promise<NotificationTemplate> {
  const { data } = await apiClient.get<ApiResponseWrapper<NotificationTemplate>>(
    notificationTemplateByIdPath(id)
  )
  if (data?.data && typeof data.data === 'object' && data.data._id) {
    return data.data
  }
  throw new Error('Notification template not found')
}

export async function updateNotificationTemplate(
  id: string,
  payload: NotificationTemplateUpdatePayload
): Promise<NotificationTemplate> {
  const { data } = await apiClient.put<ApiResponseWrapper<NotificationTemplate>>(
    notificationTemplateByIdPath(id),
    payload
  )
  if (data?.data && typeof data.data === 'object' && data.data._id) {
    return data.data
  }
  throw new Error('Failed to update notification template')
}
